import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toPng } from 'html-to-image'
import DryIceLabel, { DryIceLabelData, LABEL_H, LABEL_W, LabelParty } from './DryIceLabel'

/** Internal-only tool. Pulls real orders via the list-orders Netlify Function
 *  and renders a print-ready 4x6 Class 9 / UN1845 dry-ice label. */

const LABELS_PASSWORD = 'T@ils2025'
const SESSION_KEY = 'labels-authed'

type Source = 'taste' | 'wholesale'

interface Order {
  id: string
  source: Source
  orderNumber: string
  fullName: string
  email: string
  shippingAddress: { street: string; apt: string; city: string; state: string; zip: string }
  status: string
  totalAmount: number
  createdAt: string
  productLabel: string
}

// Ship-from origin (Sun Valley, CA 91352 per ops docs). Editable in the UI.
const DEFAULT_SHIPPER: LabelParty = {
  name: 'TAILTATIONS LLC',
  line1: '7782 San Fernando Rd',
  line2: 'Sun Valley, CA 91352',
}

// 6-pack ≈ ~5 lbs of dry ice; wholesale 30-pack ≈ ~10 lbs. 2 lbs = 1 kg.
const DEFAULT_KG: Record<Source, string> = { taste: '2', wholesale: '5' }

const consigneeFromOrder = (o: Order): LabelParty => {
  const a = o.shippingAddress
  const street = [a.street, a.apt].filter(Boolean).join(', ')
  const cityLine = [[a.city, a.state].filter(Boolean).join(', '), a.zip].filter(Boolean).join(' ')
  return { name: o.fullName, line1: street, line2: cityLine }
}

const LabelsPage: React.FC = () => {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(SESSION_KEY) === '1')
  const [pwInput, setPwInput] = useState('')
  const [pwError, setPwError] = useState(false)

  const [source, setSource] = useState<Source>('taste')
  const [query, setQuery] = useState('')
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 2

  const [dryIceKg, setDryIceKg] = useState(DEFAULT_KG.taste)
  const [numPackages, setNumPackages] = useState(1)
  const [shipper, setShipper] = useState<LabelParty>(DEFAULT_SHIPPER)
  const [consignee, setConsignee] = useState<LabelParty>({ name: '', line1: '', line2: '' })

  const labelWrapRef = useRef<HTMLDivElement>(null)

  const fetchOrders = useCallback(async (src: Source, q: string) => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ source: src })
      if (q.trim()) params.set('q', q.trim())
      const res = await fetch(`/.netlify/functions/list-orders?${params.toString()}`)
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`)
      setOrders(json.orders || [])
    } catch (e: any) {
      setError(e.message || 'Failed to load orders')
      setOrders([])
    } finally {
      setLoading(false)
    }
  }, [])

  // Load on source change + debounce search.
  useEffect(() => {
    if (!authed) return
    setSelectedId(null)
    setPage(0)
    setDryIceKg(DEFAULT_KG[source])
    const t = setTimeout(() => fetchOrders(source, query), 250)
    return () => clearTimeout(t)
  }, [source, query, fetchOrders, authed])

  const selected = useMemo(
    () => orders.find((o) => o.id === selectedId) || null,
    [orders, selectedId]
  )

  const totalPages = Math.ceil(orders.length / PAGE_SIZE)
  const pagedOrders = orders.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)

  const handleSelect = (o: Order) => {
    setSelectedId(o.id)
    setConsignee(consigneeFromOrder(o))
  }

  const labelData: DryIceLabelData = {
    dryIceKg,
    numPackages,
    shipper,
    consignee,
    orderNumber: selected?.orderNumber,
  }

  const downloadPng = useCallback(async () => {
    const node = document.getElementById('dry-ice-label')
    if (!node) return
    // 300 DPI / 96 CSS-dpi ≈ 3.125x for a crisp thermal print.
    const scale = 300 / 96
    const dataUrl = await toPng(node, {
      pixelRatio: scale,
      width: LABEL_W,
      height: LABEL_H,
      backgroundColor: '#ffffff',
      cacheBust: true,
    })
    const a = document.createElement('a')
    const fname = selected?.orderNumber
      ? `dryice-label-${selected.orderNumber}.png`
      : 'dryice-label.png'
    a.href = dataUrl
    a.download = fname
    a.click()
  }, [selected])

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #d4d4d8',
    borderRadius: 6,
    fontSize: 14,
    boxSizing: 'border-box',
  }

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (pwInput === LABELS_PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, '1')
      setAuthed(true)
      setPwError(false)
    } else {
      setPwError(true)
    }
  }

  if (!authed) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f4f4f5' }}>
        <form onSubmit={handlePasswordSubmit} style={{ width: 320, background: '#fff', padding: 32, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <a href="/" style={{ display: 'block', textAlign: 'center', marginBottom: 20 }}>
            <img src="/tailtations.svg" alt="Tailtations" style={{ height: 64 }} />
          </a>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: '#18181b', textAlign: 'center', margin: '0 0 16px' }}>
            Label Generator
          </h2>
          <input
            type="password"
            value={pwInput}
            onChange={(e) => { setPwInput(e.target.value); setPwError(false) }}
            placeholder="Password"
            autoFocus
            style={{
              width: '100%',
              padding: '10px 12px',
              border: `1px solid ${pwError ? '#b91c1c' : '#d4d4d8'}`,
              borderRadius: 6,
              fontSize: 14,
              boxSizing: 'border-box',
              marginBottom: 12,
            }}
          />
          {pwError && <p style={{ color: '#b91c1c', fontSize: 13, margin: '0 0 12px' }}>Incorrect password.</p>}
          <button
            type="submit"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 6,
              border: 'none',
              background: '#8a3134',
              color: '#fff',
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Enter
          </button>
        </form>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f4f4f5', padding: 24 }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <a href="/">
            <img src="/tailtations.svg" alt="Tailtations" style={{ height: 80 }} />
          </a>
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 4px', color: '#18181b' }}>
          Dry Ice Label Generator
        </h1>
        <p style={{ color: '#71717a', margin: '0 0 20px', fontSize: 14 }}>
          Class 9 · UN1845 · 4&times;6 — select a paid order, then download the PNG for your label
          printer.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
          {/* ---- Left: controls ---- */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Source toggle */}
            <div style={{ display: 'flex', gap: 8 }}>
              {(['taste', 'wholesale'] as Source[]).map((s) => (
                <button
                  key={s}
                  onClick={() => setSource(s)}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid',
                    borderColor: source === s ? '#8a3134' : '#d4d4d8',
                    background: source === s ? '#8a3134' : '#fff',
                    color: source === s ? '#fff' : '#3f3f46',
                    fontWeight: 700,
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                  }}
                >
                  {s === 'taste' ? 'Taste Orders' : 'Wholesale Orders'}
                </button>
              ))}
            </div>

            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search order #, name, or email…"
              style={inputStyle}
            />

            {/* Order list */}
            <div
              style={{
                border: '1px solid #e4e4e7',
                borderRadius: 8,
                background: '#fff',
                maxHeight: 260,
                overflowY: 'auto',
              }}
            >
              {loading && <div style={{ padding: 14, color: '#71717a' }}>Loading…</div>}
              {error && (
                <div style={{ padding: 14, color: '#b91c1c', fontSize: 13 }}>{error}</div>
              )}
              {!loading && !error && orders.length === 0 && (
                <div style={{ padding: 14, color: '#71717a' }}>No paid orders found.</div>
              )}
              {pagedOrders.map((o) => {
                const active = o.id === selectedId
                return (
                  <button
                    key={o.id}
                    onClick={() => handleSelect(o)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 12px',
                      border: 'none',
                      borderBottom: '1px solid #f4f4f5',
                      background: active ? '#fef2f2' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#18181b' }}>
                      {o.fullName}{' '}
                      <span style={{ fontWeight: 400, color: '#a1a1aa' }}>· {o.productLabel}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#71717a' }}>
                      {o.orderNumber} · {o.shippingAddress.city}, {o.shippingAddress.state}
                    </div>
                  </button>
                )
              })}
            </div>

            {/* Pagination */}
            {orders.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: '1px solid #d4d4d8',
                    background: page === 0 ? '#f4f4f5' : '#fff',
                    color: page === 0 ? '#a1a1aa' : '#3f3f46',
                    fontWeight: 700,
                    cursor: page === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 13,
                  }}
                >
                  ← Prev
                </button>
                <span style={{ fontSize: 13, color: '#71717a' }}>
                  Page {page + 1} of {totalPages} ({orders.length} orders)
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: '1px solid #d4d4d8',
                    background: page >= totalPages - 1 ? '#f4f4f5' : '#fff',
                    color: page >= totalPages - 1 ? '#a1a1aa' : '#3f3f46',
                    fontWeight: 700,
                    cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
                    fontSize: 13,
                  }}
                >
                  Next →
                </button>
              </div>
            )}

            {/* Editable fields */}
            <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, padding: 16 }}>
              <Field label="Dry Ice (kg per pkg)">
                <input value={dryIceKg} onChange={(e) => setDryIceKg(e.target.value)} style={inputStyle} />
              </Field>

              <Field label="Number of Packages">
                <input
                  type="number"
                  min={1}
                  value={numPackages}
                  onChange={(e) => setNumPackages(Math.max(1, Number(e.target.value) || 1))}
                  style={inputStyle}
                />
              </Field>

              <PartyEditor title="Shipper (FROM)" party={shipper} onChange={setShipper} inputStyle={inputStyle} />
              <PartyEditor title="Consignee (TO)" party={consignee} onChange={setConsignee} inputStyle={inputStyle} />
            </div>

            <button
              onClick={downloadPng}
              disabled={!consignee.name}
              style={{
                padding: '12px 16px',
                borderRadius: 8,
                border: 'none',
                background: consignee.name ? '#8a3134' : '#d4d4d8',
                color: '#fff',
                fontWeight: 800,
                fontSize: 15,
                cursor: consignee.name ? 'pointer' : 'not-allowed',
              }}
            >
              Download 4×6 PNG (300 DPI)
            </button>
          </div>

          {/* ---- Right: live label preview ---- */}
          <div style={{ position: 'sticky', top: 24 }}>
            <div style={{ fontSize: 12, color: '#71717a', marginBottom: 8 }}>
              Preview (4&quot; × 6&quot;)
            </div>
            <div
              ref={labelWrapRef}
              style={{
                display: 'inline-block',
                boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                background: '#fff',
              }}
            >
              <DryIceLabel data={labelData} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 12, fontWeight: 700, color: '#52525b', marginBottom: 4 }}>{label}</div>
    {children}
  </div>
)

const PartyEditor: React.FC<{
  title: string
  party: LabelParty
  onChange: (p: LabelParty) => void
  inputStyle: React.CSSProperties
}> = ({ title, party, onChange, inputStyle }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 12, fontWeight: 700, color: '#52525b', marginBottom: 4 }}>{title}</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <input
        value={party.name}
        onChange={(e) => onChange({ ...party, name: e.target.value })}
        placeholder="Name"
        style={inputStyle}
      />
      <input
        value={party.line1}
        onChange={(e) => onChange({ ...party, line1: e.target.value })}
        placeholder="Street, Apt"
        style={inputStyle}
      />
      <input
        value={party.line2}
        onChange={(e) => onChange({ ...party, line2: e.target.value })}
        placeholder="City, State ZIP"
        style={inputStyle}
      />
    </div>
  </div>
)

export default LabelsPage
